import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import Collaboration from '@tiptap/extension-collaboration'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import { TaskItem, TaskList } from '@tiptap/extension-list'
import { Details, DetailsContent, DetailsSummary } from '@tiptap/extension-details'
import i18next from 'i18next'
import { common, createLowlight } from 'lowlight'
import { FloatingToolbar } from './FloatingToolbar'
import { TableToolbar } from './TableToolbar'
import { BlockHandle } from './BlockHandle'
import {
  SLASH_COMMAND_KEYS,
  createSlashCommandExtension,
} from './SlashCommandExtension'
import { MentionExtension, extractMentionsFromContent } from './MentionExtension'
import { EntityLinkExtension } from './EntityLinkExtension'
import { CommentMarkExtension } from './CommentMarkExtension'
import { CalloutExtension } from './CalloutExtension'
import { createMolyActionsExtension } from './editorActions'
import type { EditorActions } from './editorActions'
import type { ReactNode } from 'react'
import type { Editor, JSONContent } from '@tiptap/core'
import type { FloatingFormatToolbarItem } from './FloatingToolbar'
import type { HocuspocusProvider } from '@hocuspocus/provider'
import type * as Y from 'yjs'
import { cn } from '@/lib/utils'

export type { FloatingFormatToolbarItem }
export { extractMentionsFromContent }
export { extractEntityLinksFromContent } from './entityLinkUtils'

const lowlight = createLowlight(common)
const EMPTY_DOC: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
}

interface TiptapRichTextEditorProps {
  namespace: string
  initialContent?: string
  format?: 'tiptap' | 'markdown' | 'plain'
  editable?: boolean
  placeholder?: ReactNode
  className?: string
  contentEditableClassName?: string
  onContentChange?: (content: string, editor: Editor) => void
  onError?: (error: Error) => void
  /**
   * Called with files (e.g. an image) pasted into the editor. When provided,
   * file pastes are intercepted via ProseMirror's `handlePaste` and forwarded
   * here instead of being dropped — callers typically upload them as
   * attachments. Text/HTML pastes are unaffected.
   */
  onPasteFiles?: (files: Array<File>) => void
  floatingToolbar?: boolean
  enableMentions?: boolean
  enableEntityLinks?: boolean
  enableSlashCommands?: boolean
  /**
   * Enables the table *authoring* affordances — the `/table` slash command
   * and the row/column toolbar. Off by default: one editor component serves
   * every surface, and a table is rarely wanted in, say, a comment.
   *
   * This deliberately does **not** gate the table nodes themselves, which are
   * always registered. Content reaches a surface from more places than its own
   * toolbar — MCP can write a markdown table into a comment, and web can author
   * one that mobile later loads — and ProseMirror silently drops nodes missing
   * from its schema, persisting the damage on the next save. Every surface can
   * therefore render a table; only some can create one.
   */
  enableTables?: boolean
  allowedSlashCommands?: Array<string>
  ariaLabel?: string
  toolbarItems?: Array<FloatingFormatToolbarItem>
  placeholderClassName?: string
  // Collaboration props
  collaborative?: boolean
  ydoc?: Y.Doc
  provider?: HocuspocusProvider | null
  userName?: string
  userColor?: string
  // Inline comments
  enableComments?: boolean
  onAddComment?: (anchorId: string) => void
  onCommentClick?: (anchorId: string) => void
  onEditorReady?: (editor: Editor | null) => void
  /** App actions offered in the slash menu / selection toolbar (create task…). */
  actions?: EditorActions
}

export function buildPlainTextEditorStateJSON(text: string): string {
  const doc: JSONContent = {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: text ? [{ type: 'text', text }] : [],
      },
    ],
  }
  return JSON.stringify(doc)
}

function parseInitialContent(
  content: string | undefined,
  format: string | undefined,
): JSONContent | string | undefined {
  if (!content) return undefined

  if (format === 'markdown' || format === 'plain') {
    // For markdown/plain, let Tiptap parse it as HTML-like content
    // or provide as plain text in a paragraph
    return {
      type: 'doc',
      content: content.split('\n').map((line) => ({
        type: 'paragraph',
        content: line ? [{ type: 'text', text: line }] : [],
      })),
    }
  }

  // Default: tiptap JSON format
  try {
    const trimmed = content.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return JSON.parse(content)
    }
    // Plain text fallback
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [{ type: 'text', text: content }],
        },
      ],
    }
  } catch {
    return {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [{ type: 'text', text: content }],
        },
      ],
    }
  }
}

export function TiptapRichTextEditor({
  namespace,
  initialContent,
  format,
  editable = true,
  placeholder = 'Start typing...',
  className,
  contentEditableClassName,
  onContentChange,
  onError,
  onPasteFiles,
  floatingToolbar = true,
  enableMentions = false,
  enableEntityLinks = false,
  enableSlashCommands = true,
  enableTables = false,
  allowedSlashCommands,
  ariaLabel = 'Rich text editor',
  toolbarItems,
  placeholderClassName: _placeholderClassName,
  collaborative = false,
  ydoc,
  provider,
  userName,
  userColor,
  enableComments = false,
  onAddComment,
  onCommentClick,
  onEditorReady,
  actions,
}: TiptapRichTextEditorProps) {
  const placeholderText =
    typeof placeholder === 'string' ? placeholder : 'Start typing...'

  const [, setForceUpdate] = useState(0)

  // `editorProps` is built once (the editor only re-creates on `namespace`
  // change), so read the paste handler through a ref to avoid a stale closure.
  const onPasteFilesRef = useRef(onPasteFiles)
  onPasteFilesRef.current = onPasteFiles
  const actionsRef = useRef<EditorActions>(actions ?? {})
  actionsRef.current = actions ?? {}

  const extensions = useMemo(() => {
    const ext: Array<any> = [
      StarterKit.configure({
        codeBlock: false, // Use lowlight version instead
        // Disable undo/redo when collaborative (Y.js handles it). TipTap 3
        // calls this `undoRedo`; the v2 name `history` was silently ignored.
        ...(collaborative ? { undoRedo: false } : {}),
      }),
      createMolyActionsExtension(actionsRef),
      Placeholder.configure({
        // An empty toggle title needs its own hint; everything else keeps the
        // editor-level placeholder.
        placeholder: ({ node }) =>
          node.type.name === 'detailsSummary' ? i18next.t('common:editor.toggle.placeholder') : placeholderText,
        includeChildren: true,
        emptyEditorClass: 'is-editor-empty',
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
    ]

    // Registered unconditionally — see the `enableTables` prop doc. Schema
    // parity is a correctness requirement; `enableTables` only decides who can
    // author a table.
    ext.push(
      Table.configure({
        // Resize handles are a fine-pointer affordance; on touch the drag
        // fights horizontal scrolling of the table itself.
        resizable: false,
        // With resizing off there's no NodeView, so the `.tableWrapper` div
        // only exists if renderHTML emits it — and that div is the per-table
        // horizontal scroll container that keeps a wide table from widening
        // the whole document.
        renderWrapper: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      // To-do lists: registered everywhere for the same schema-parity reason
      // as tables (mobile's TenTap and the API both know taskList/taskItem).
      TaskList,
      TaskItem.configure({ nested: true }),
      // Callouts and toggles: same parity rule (mobile webview + API know
      // callout / details / detailsSummary / detailsContent). Toggles start
      // closed and their open state is per-viewer, not saved in the doc.
      CalloutExtension,
      Details.configure({
        persist: false,
        HTMLAttributes: { class: 'moly-toggle' },
        renderToggleButton: ({ element, isOpen }) => {
          element.setAttribute(
            'aria-label',
            i18next.t(isOpen ? 'common:editor.toggle.collapse' : 'common:editor.toggle.expand'),
          )
        },
      }),
      DetailsSummary,
      DetailsContent,
    )

    if (enableSlashCommands) {
      const allowedKeys = allowedSlashCommands ?? SLASH_COMMAND_KEYS
      // Surfaces that can't author tables shouldn't offer /table.
      ext.push(
        createSlashCommandExtension(
          enableTables
            ? allowedKeys
            : allowedKeys.filter((key) => key !== 'table'),
        ),
      )
    }

    if (enableMentions) {
      ext.push(MentionExtension)
    }

    if (enableEntityLinks) {
      ext.push(EntityLinkExtension)
    }

    if (enableComments) {
      ext.push(CommentMarkExtension)
    }

    if (collaborative && ydoc) {
      ext.push(
        Collaboration.configure({
          document: ydoc,
        }),
      )

      if (provider) {
        ext.push(
          CollaborationCaret.configure({
            provider,
            user: {
              name: userName || 'Anonymous',
              color: userColor || '#958DF1',
            },
          }),
        )
      }
    }

    return ext
  }, [
    placeholderText,
    enableSlashCommands,
    allowedSlashCommands,
    enableMentions,
    enableEntityLinks,
    enableComments,
    enableTables,
    collaborative,
    ydoc,
    provider,
    userName,
    userColor,
  ])

  // For collaborative mode, content comes from Y.js, not initialContent
  const parsedContent = useMemo(
    () => (collaborative ? undefined : parseInitialContent(initialContent, format)),
    [initialContent, format, collaborative],
  )
  const lastAppliedContentRef = useRef<string | undefined>(initialContent)

  const editor = useEditor(
    {
      extensions,
      content: parsedContent,
      editable,
      editorProps: {
        // Intercept file pastes (e.g. screenshots) so they can be uploaded as
        // attachments instead of being silently dropped — Tiptap has no Image
        // extension here. Returning `true` tells ProseMirror we handled it.
        handlePaste: (_view, event) => {
          const handler = onPasteFilesRef.current
          if (!handler) return false
          const items = event.clipboardData?.items
          if (!items || items.length === 0) return false
          const files: Array<File> = []
          for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item?.kind === 'file') {
              const file = item.getAsFile()
              if (file) files.push(file)
            }
          }
          if (files.length === 0) return false
          handler(files)
          return true
        },
        attributes: {
          'aria-label': ariaLabel,
          class: cn(
            'min-h-[100px] w-full bg-transparent py-3 outline-none prose prose-sm max-w-none',
            'prose-p:mb-3 prose-p:leading-relaxed prose-p:text-sm prose-p:text-foreground',
            'prose-headings:font-semibold prose-headings:text-foreground',
            'prose-h1:text-3xl prose-h1:leading-tight prose-h1:mb-4',
            'prose-h2:text-2xl prose-h2:leading-snug prose-h2:mb-3',
            'prose-h3:text-xl prose-h3:leading-snug prose-h3:mb-2.5',
            'prose-h4:text-lg prose-h4:leading-snug prose-h4:mb-2',
            'prose-strong:font-semibold prose-strong:text-foreground',
            'prose-ul:list-disc prose-ul:ml-5 prose-ul:mb-3',
            'prose-ol:list-decimal prose-ol:ml-5 prose-ol:mb-3',
            'prose-li:mb-1 prose-li:text-sm prose-li:text-foreground',
            'prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-mono prose-code:text-[0.875em] prose-code:font-normal prose-code:text-foreground',
            // Typography wraps inline code in literal backticks via ::before/::after.
            'prose-code:before:content-none prose-code:after:content-none',
            '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:rounded-none [&_pre_code]:text-inherit',
            'prose-hr:my-6 prose-hr:border-t prose-hr:border-border',
            'prose-blockquote:border-l-4 prose-blockquote:border-border prose-blockquote:pl-4 prose-blockquote:text-muted-foreground',
            contentEditableClassName,
          ),
        },
      },
      onUpdate: ({ editor: ed }) => {
        // In collaborative mode, Y.js handles sync — skip JSON output
        if (collaborative) return

        if (onContentChange) {
          try {
            const json = JSON.stringify(ed.getJSON())
            onContentChange(json, ed)
          } catch (error) {
            onError?.(error instanceof Error ? error : new Error(String(error)))
          }
        }
      },
      onCreate: ({ editor: ed }) => {
        // Set aria-label on the editable element
        const element = ed.view.dom
        element.setAttribute('aria-label', ariaLabel)
        // Force re-render so parent can access the editor instance
        setForceUpdate((n) => n + 1)
      },
    },
    [namespace], // Re-create editor when namespace changes
  )

  useEffect(() => {
    if (!editor || collaborative) return
    if (lastAppliedContentRef.current === initialContent) return
    if (editor.isFocused) return

    lastAppliedContentRef.current = initialContent

    try {
      editor.commands.setContent(parsedContent ?? EMPTY_DOC, { emitUpdate: false })
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)))
    }
  }, [editor, initialContent, parsedContent, collaborative, onError])

  // Update editable state
  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable)
    }
  }, [editor, editable])

  // Notify parent when editor instance becomes available (or is destroyed)
  useEffect(() => {
    if (!onEditorReady) return
    onEditorReady(editor ?? null)
    return () => {
      onEditorReady(null)
    }
  }, [editor, onEditorReady])

  // Open existing comment threads when a comment-highlight span is clicked.
  // Use a plain DOM listener on the editor root so it works regardless of
  // whether event.target is a Text node or the span itself.
  useEffect(() => {
    if (!editor || !onCommentClick) return
    const dom = editor.view.dom
    const handler = (event: MouseEvent) => {
      let node: Node | null = event.target as Node | null
      if (node && node.nodeType === Node.TEXT_NODE) {
        node = node.parentElement
      }
      const span =
        node instanceof Element
          ? (node.closest('span[data-comment-id]') as HTMLElement | null)
          : null
      if (!span) return
      const commentId = span.getAttribute('data-comment-id')
      if (!commentId) return
      onCommentClick(commentId)
    }
    dom.addEventListener('click', handler)
    return () => {
      dom.removeEventListener('click', handler)
    }
  }, [editor, onCommentClick])

  const handleAddComment = useCallback(() => {
    if (!editor || !onAddComment) return
    const { from, to } = editor.state.selection
    if (from === to) return // No text selected

    const anchorId = crypto.randomUUID()
    editor.chain().focus().setCommentHighlight({ commentId: anchorId }).run()
    onAddComment(anchorId)
  }, [editor, onAddComment])

  if (!editor) {
    return null
  }

  return (
    <div className={cn('relative grid gap-2 min-w-0', className)}>
      {floatingToolbar && editable ? (
        <FloatingToolbar
          editor={editor}
          items={toolbarItems}
          // Doc-like surfaces (full slash menu) get block + app controls.
          enableBlockControls={enableSlashCommands && !allowedSlashCommands}
          enableComments={enableComments}
          onAddComment={enableComments ? handleAddComment : undefined}
        />
      ) : null}
      {enableTables && editable ? <TableToolbar editor={editor} /> : null}
      {editable && enableSlashCommands && !allowedSlashCommands ? <BlockHandle editor={editor} /> : null}
      <div className="grid gap-2 min-w-0 overflow-hidden">
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .tiptap, .ProseMirror {
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          pointer-events: none;
          height: 0;
          color: var(--color-muted-foreground, #94a3b8);
          font-size: 0.875rem;
        }
        .comment-highlight {
          background-color: rgb(254 240 138 / 0.6);
          border-bottom: 2px solid rgb(234 179 8 / 0.5);
          cursor: pointer;
          padding: 1px 0;
        }
        .comment-highlight:hover {
          background-color: rgb(254 240 138 / 0.9);
        }
        .comment-highlight--resolved {
          background-color: rgb(226 232 240 / 0.3);
          border-bottom-color: rgb(148 163 184 / 0.3);
        }
        .collaboration-caret__caret {
          border-left: 1px solid #0d0d0d;
          border-right: 1px solid #0d0d0d;
          margin-left: -1px;
          margin-right: -1px;
          pointer-events: none;
          position: relative;
          word-break: normal;
        }
        /* To-do lists (design 7a): checkbox in the margin, done items dim. */
        .ProseMirror ul[data-type='taskList'] {
          list-style: none;
          padding-left: 0.125rem;
          margin: 0.25rem 0;
        }
        .ProseMirror ul[data-type='taskList'] li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .ProseMirror ul[data-type='taskList'] li > label {
          flex: none;
          margin-top: 0.2em;
          user-select: none;
        }
        .ProseMirror ul[data-type='taskList'] li > label input[type='checkbox'] {
          width: 0.95rem;
          height: 0.95rem;
          accent-color: var(--foreground);
          cursor: pointer;
        }
        .ProseMirror ul[data-type='taskList'] li > div {
          flex: 1;
          min-width: 0;
        }
        .ProseMirror ul[data-type='taskList'] li {
          margin: 0.125rem 0;
        }
        .ProseMirror ul[data-type='taskList'] li > div > p {
          margin: 0;
        }
        .ProseMirror ul[data-type='taskList'] li[data-checked='true'] > div {
          color: var(--muted-foreground);
          text-decoration: line-through;
        }
        /* Callouts (design 7a): tinted box with a variant icon. */
        .moly-callout {
          display: flex;
          gap: 0.625rem;
          margin: 0.5rem 0;
          padding: 0.75rem 0.875rem;
          border-radius: 10px;
          background: var(--callout-bg);
          --callout-bg: color-mix(in oklab, var(--brand) 9%, var(--card));
          --callout-icon: var(--brand);
        }
        .moly-callout[data-variant='tip'] {
          --callout-bg: color-mix(in oklab, #3f8f5a 11%, var(--card));
          --callout-icon: #3f8f5a;
        }
        .moly-callout[data-variant='warning'] {
          --callout-bg: color-mix(in oklab, #c98a1b 13%, var(--card));
          --callout-icon: #b7791f;
        }
        .moly-callout[data-variant='danger'] {
          --callout-bg: color-mix(in oklab, #c2413b 11%, var(--card));
          --callout-icon: #c2413b;
        }
        .moly-callout__icon {
          flex: none;
          display: flex;
          height: 1.6em;
          align-items: center;
          color: var(--callout-icon);
        }
        .moly-callout__body {
          flex: 1;
          min-width: 0;
        }
        .moly-callout__body > div > :first-child { margin-top: 0; }
        .moly-callout__body > div > :last-child { margin-bottom: 0; }
        /* Toggles: chevron + title, body hidden until opened. */
        .ProseMirror .moly-toggle {
          display: flex;
          gap: 0.25rem;
          margin: 0.25rem 0;
        }
        .ProseMirror .moly-toggle > button {
          flex: none;
          display: flex;
          width: 1.25rem;
          height: 1.6em;
          align-items: center;
          justify-content: center;
          border-radius: 0.25rem;
          color: var(--muted-foreground);
          cursor: pointer;
        }
        .ProseMirror .moly-toggle > button:hover { background: var(--accent); }
        .ProseMirror .moly-toggle > button::before {
          content: '';
          width: 0.4rem;
          height: 0.4rem;
          border-right: 1.5px solid currentColor;
          border-bottom: 1.5px solid currentColor;
          transform: rotate(-45deg);
          transition: transform 120ms ease;
        }
        .ProseMirror .moly-toggle.is-open > button::before { transform: rotate(45deg); }
        .ProseMirror .moly-toggle > div {
          flex: 1;
          min-width: 0;
        }
        .ProseMirror .moly-toggle summary {
          display: block;
          color: var(--foreground);
          font-weight: 500;
          list-style: none;
          cursor: text;
        }
        .ProseMirror .moly-toggle summary.is-empty::before {
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
          color: var(--muted-foreground);
          font-weight: 400;
        }
        .ProseMirror .moly-toggle [data-type='detailsContent'] > :first-child { margin-top: 0.25rem; }
        /* Static (non-node-view) entity links, e.g. read-only renders. */
        span[data-entity-link]:not([data-node-view-wrapper]) {
          display: inline-flex;
          align-items: center;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background-color: var(--card);
          padding: 0 0.375rem;
          font-size: 0.9em;
          cursor: pointer;
        }
        /*
         * Tables. The wrapper is the scroll container: a wide table scrolls
         * inside its own box instead of widening the document, which is what
         * keeps the surrounding layout (and the page body) from scrolling
         * sideways on narrow viewports.
         */
        .ProseMirror .tableWrapper {
          overflow-x: auto;
          max-width: 100%;
          margin: 0.75rem 0;
        }
        .ProseMirror table {
          border-collapse: collapse;
          table-layout: fixed;
          /* Grow past the container rather than crushing columns, but still
             fill it when the table is narrow. */
          width: max-content;
          min-width: 100%;
          margin: 0;
        }
        .ProseMirror th,
        .ProseMirror td {
          position: relative;
          border: 1px solid rgb(226 232 240);
          padding: 0.375rem 0.5rem;
          min-width: 6rem;
          vertical-align: top;
          text-align: left;
        }
        .ProseMirror th {
          background-color: rgb(248 250 252);
          font-weight: 600;
        }
        .ProseMirror th > p,
        .ProseMirror td > p {
          margin: 0;
        }
        .ProseMirror th > p + p,
        .ProseMirror td > p + p {
          margin-top: 0.25rem;
        }
        /* prosemirror-tables marks cells in a cell-selection with this class. */
        .ProseMirror .selectedCell::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgb(59 130 246 / 0.12);
          pointer-events: none;
        }
        .collaboration-caret__label {
          border-radius: 3px 3px 3px 0;
          color: #0d0d0d;
          font-size: 12px;
          font-style: normal;
          font-weight: 600;
          left: -1px;
          line-height: normal;
          padding: 0.1rem 0.3rem;
          position: absolute;
          top: -1.4em;
          user-select: none;
          white-space: nowrap;
        }
      `}</style>
    </div>
  )
}
